import { authService } from "./auth";

// Types
type ApiRequestOptions = globalThis.RequestInit & {
  responseType?: 'json' | 'blob' | 'text';
};

function toHeaderRecord(headersInit?: HeadersInit): Record<string, string> {
  const normalized = new Headers(headersInit);
  const record: Record<string, string> = {};
  normalized.forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL;
  if (configured && configured.trim()) {
    return normalizeBaseUrl(configured.trim());
  }

  if (typeof window === 'undefined') {
    return 'http://localhost:8080';
  }

  const hostname = window.location.hostname.toLowerCase();
  const origin = window.location.origin;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8080';
  }

  const hostedMap: Array<{ suffix: string; api: string }> = [
    { suffix: 'admin.d.xtnd.legal', api: 'https://api.d.xtnd.legal' },
    { suffix: 'admin.u.xtnd.legal', api: 'https://api.u.xtnd.legal' },
    { suffix: 'admin.uat.xtnd.legal', api: 'https://api.u.xtnd.legal' },
    { suffix: 'admin.p.xtnd.legal', api: 'https://api.p.xtnd.legal' },
    { suffix: 'admin.xtnd.legal', api: 'https://api.p.xtnd.legal' },
  ];

  for (const mapping of hostedMap) {
    if (hostname === mapping.suffix || hostname.endsWith(`.${mapping.suffix}`)) {
      return mapping.api;
    }
  }

  // Safe fallback for environments fronted by same-origin reverse proxy.
  return `${normalizeBaseUrl(origin)}/api`;
}

// API configuration
const API_BASE_URL = resolveApiBaseUrl();

// API client configuration
const apiClient = {
  baseURL: API_BASE_URL,
};

// Generic API request function
async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}, ignoreAuth = false): Promise<T> {
  const url = `${apiClient.baseURL}${endpoint}`;
  const { responseType = 'json', ...fetchOptions } = options;

  let accessToken;
  if (!ignoreAuth) {
    accessToken = await authService.getAccessToken();
  }

  const headers = toHeaderRecord(fetchOptions.headers as HeadersInit | undefined);

  const hasBody = typeof fetchOptions.body !== 'undefined' && fetchOptions.body !== null;
  const hasContentTypeHeader = Object.keys(headers).some(key => key.toLowerCase() === 'content-type');
  if (hasBody && !hasContentTypeHeader && !(fetchOptions.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const config: ApiRequestOptions = {
    ...fetchOptions,
    ...(Object.keys(headers).length > 0 ? { headers } : {}),
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    // For blob requests, try to parse as JSON first for error messages
    if (responseType === 'blob') {
      try {
        const errorData = await response.json();
        const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      } catch (parseError) {
        // If JSON parsing fails, fall back to text
        const error = await response.text();
        throw new Error(error || `HTTP error! status: ${response.status}`);
      }
    } else {
      const error = await response.text();
      throw new Error(error || `HTTP error! status: ${response.status}`);
    }
  }

  // Handle different response types
  switch (responseType) {
    case 'blob':
      // For blob responses, also return headers so we can extract filename
      const blob = await response.blob();
      return {
        data: blob,
        headers: Object.fromEntries(response.headers.entries())
      } as T;
    case 'text':
      return response.text() as Promise<T>;
    case 'json':
    default: {
      // 204 No Content and other empty-body responses (e.g. POSTs that
      // return 202 Accepted without a body) shouldn't crash JSON parsing.
      // `response.json()` throws "Unexpected end of JSON input" on empty
      // bodies; fall through to undefined for those.
      if (response.status === 204) return undefined as unknown as T;
      const text = await response.text();
      if (!text) return undefined as unknown as T;
      return JSON.parse(text) as T;
    }
  }
}

// API methods
export const api = {
  get: <T>(endpoint: string, options?: ApiRequestOptions, ignoreAuth = false) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }, ignoreAuth),

  post: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions, ignoreAuth = false) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, ignoreAuth),

  put: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions, ignoreAuth = false) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }, ignoreAuth),

  patch: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions, ignoreAuth = false) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }, ignoreAuth),

  delete: <T>(endpoint: string, options?: ApiRequestOptions, ignoreAuth = false) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }, ignoreAuth),
};

// API endpoints
export const endpoints = {
  // Health check
  health: '/health',

  // Authentication
  auth: {
    login: '/auth/login',
    loginUrl: '/auth/login-url',
    adminLoginUrl: '/auth/admin/login-url',
    callback: '/auth/callback',
    adminCallback: '/auth/admin/callback',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
  },

  // PDF Service
  pdf: {
    // Templates
    templates: {
      list: '/pdf/templates',
      create: '/pdf/templates',
      get: (id: string) => `/pdf/templates/${id}`,
      update: (id: string) => `/pdf/templates/${id}`,
      delete: (id: string) => `/pdf/templates/${id}`,
      activate: (id: string) => `/pdf/templates/${id}/activate`,
      deactivate: (id: string) => `/pdf/templates/${id}/deactivate`,
    },
    // Images
    images: {
      list: '/pdf/images',
      create: '/pdf/images',
      get: (id: string) => `/pdf/images/${id}`,
      update: (id: string) => `/pdf/images/${id}`,
      delete: (id: string) => `/pdf/images/${id}`,
      upload: '/pdf/images/upload',
    },
    // PDF Generation
    generate: {
      create: '/pdf/generate',
      status: (id: string) => `/pdf/generate/${id}/status`,
      download: (id: string) => `/pdf/generate/${id}/download`,
    },
    // Test Harness
    test: {
      generate: '/pdf/test/generate',
      templates: '/pdf/test/templates',
      images: '/pdf/test/images',
    },
  },

  // Invoices
  invoices: {
    list: '/invoices',
    create: '/invoices',
    get: (id: string) => `/invoices/${id}`,
    update: (id: string) => `/invoices/${id}`,
    delete: (id: string) => `/invoices/${id}`,
    export: '/invoices/export',
  },

  // Settings
  settings: {
    get: '/settings',
    update: '/settings',
  },

  // Juniper Query
  juniperQuery: {
    // Transactions
    transactions: {
      list: '/juniper-query/transactions',
      get: (id: string) => `/juniper-query/transactions/${id}`,
    },
    // Matters
    matters: {
      get: (id: string) => `/juniper-query/matters/${id}`,
    },
    // Health
    health: '/juniper-query/health',
  },
};
